export const calculateTotals = (items) => {
  let subtotal = 0;
  let totalTax = 0;

  const calculatedItems = items.map((item) => {
    const itemType = item.itemType || 'honoraire';

    let taxPercent;
    if (typeof item.tax === 'number' && !Number.isNaN(item.tax)) {
      taxPercent = item.tax;
    } else if (itemType === 'honoraire') {
      taxPercent = 19;
    } else {
      taxPercent = 0;
    }

    const tvaRatio = (taxPercent || 0) / 100;

    const quantity = parseInt(item.qty || item.quantity || 1);
    const priceHT = parseFloat(item.price || item.priceHT || 0);

    const totalHT = Math.round(quantity * priceHT * 1000) / 1000;
    const totalTVA = Math.round(totalHT * tvaRatio * 1000) / 1000;
    const totalTTC = Math.round((totalHT + totalTVA) * 1000) / 1000;

    subtotal += totalHT;
    totalTax += totalTVA;

    return {
      ...item,
      tax: taxPercent,
      tva: tvaRatio,
      quantity,
      priceHT,
      totalHT,
      totalTVA,
      totalTTC,
      itemType,
      patientName: item.patientName || '',
      patientIdNumber: item.patientIdNumber || '',
    };
  });

  return {
    subtotal: Math.round(subtotal * 1000) / 1000,
    totalTax: Math.round(totalTax * 1000) / 1000,
    total: Math.round((subtotal + totalTax) * 1000) / 1000,
    items: calculatedItems,
  };
};

export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toISOString().split('T')[0];
};

export const validateInvoiceForm = (formData, factures, toastError) => {
  if (!formData.isGlobal && !formData.clientId) {
    toastError('Veuillez sélectionner un client');
    return false;
  }

  if (!formData.dueDate) {
    toastError("Veuillez saisir une date d'échéance");
    return false;
  }

  // VALIDATION DATE CHRONOLOGIQUE
  const newDate = new Date(formData.date);
  const existingDates = factures
    .map((f) => new Date(f.date))
    .sort((a, b) => b - a);
  if (existingDates.length > 0 && newDate < existingDates[0]) {
    const lastDate = existingDates[0].toLocaleDateString();
    toastError(
      `La date ne peut pas être antérieure au ${lastDate} (dernière facture)`
    );
    return false;
  }

  const hasEmptyDescription = formData.items.some(
    (item) => !item.description || item.description.trim() === ''
  );
  if (hasEmptyDescription) {
    toastError('Tous les articles doivent avoir une description');
    return false;
  }

  const hasInvalidQty = formData.items.some(
    (item) => !item.qty || parseInt(item.qty) <= 0
  );
  if (hasInvalidQty) {
    toastError('Toutes les quantités doivent être > 0');
    return false;
  }

  if (formData.isGlobal) {
    const hasMissingPatient = formData.items.some(
      (item) => !item.patientName || item.patientName.trim() === ''
    );
    if (hasMissingPatient) {
      toastError('Tous les articles doivent avoir un nom de patient en mode global');
      return false;
    }
  }

  return true;
};

export const prepareInvoiceData = (formData, calculateTotalsFn) => {
  const finalTotals = calculateTotalsFn(formData.items);

  const itemsPayload = finalTotals.items.map((item) => ({
    description: item.description.trim(),
    quantity: item.quantity,
    priceHT: item.priceHT,
    tvaRate: item.tax,
    totalHT: item.totalHT,
    totalTVA: item.totalTVA,
    totalTTC: item.totalTTC,
    itemType: item.itemType || 'honoraire',
    patientName: item.patientName || '',
    patientIdNumber: item.patientIdNumber || '',
    stockItemId: item.stockItemId || null,
  }));

  const timbreFiscal = 1.0;

  return {
    clientId: formData.clientId,
    date: new Date(formData.date).toISOString(),
    dueDate: new Date(formData.dueDate).toISOString(),
    items: itemsPayload,
    totalHT: finalTotals.subtotal,
    totalTVA: finalTotals.totalTax,
    totalTTC: Math.round(
      (finalTotals.subtotal + finalTotals.totalTax + timbreFiscal) * 1000
    ) / 1000,
    timbreFiscal,
    status: formData.status,
    notes: formData.notes || '',
  };
};

// Fonction pour générer le HTML de la facture (pour PDF et impression)
export const generateInvoiceHTML = (facture, client, cabinetInfo) => {
  if (!facture) return '';

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(3);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const statusLabels = {
    draft: 'Brouillon',
    sent: 'Envoyée',
    paid: 'Payée',
    partial: 'Partiellement payée',
    cancelled: 'Annulée',
  };

  const subtotal = facture.totalHT || 0;
  const tva = facture.totalTVA || 0;
  const timbre = facture.timbreFiscal || 0;
  const totalTTC = facture.totalTTC || facture.total || subtotal + tva + timbre;

  const totals = {
    subtotal: formatNumber(subtotal),
    tva: formatNumber(tva),
    timbre: formatNumber(timbre),
    total: formatNumber(totalTTC),
  };

  return `
    <div class="invoice-print">
      <!-- En-tête -->
      <div class="invoice-header">
        <div class="cabinet-info">
          <h1>${cabinetInfo?.name || 'CABINET'}</h1>
          <p>${cabinetInfo?.address || ''}</p>
          <p>${cabinetInfo?.city || ''}</p>
          <p>Tél: ${cabinetInfo?.phone || ''}</p>
          <p>Email: ${cabinetInfo?.email || ''}</p>
          <p>Matricule Fiscale: ${cabinetInfo?.taxId || ''}</p>
        </div>

        <div class="invoice-title">
          <h2>FACTURE</h2>
          <div class="invoice-number">
            <strong>N°: ${facture.number || ''}</strong>
          </div>
        </div>
      </div>

      <!-- Détails facture -->
      <div class="invoice-details">
        <div class="client-info">
          <h3>CLIENT</h3>
          <p><strong>${client?.companyName || 'Client'}</strong></p>
          <p>${client?.contactName ? `Contact: ${client.contactName}` : ''}</p>
          <p>${client?.address || ''}</p>
          <p>${client?.taxId ? `Matricule Fiscal: ${client.taxId}` : ''}</p>
        </div>

        <div class="invoice-info">
          <h3>DÉTAILS</h3>
          <p><strong>Date:</strong> ${formatDate(facture.date)}</p>
          <p><strong>Échéance:</strong> ${formatDate(facture.dueDate)}</p>
          <p><strong>Statut:</strong> ${
            statusLabels[facture.status] || facture.status
          }</p>
        </div>
      </div>

      <!-- Articles -->
      <table class="items-table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Qté</th>
            <th>Prix HT</th>
            <th>TVA %</th>
            <th>Montant HT</th>
            <th>Montant TTC</th>
          </tr>
        </thead>
        <tbody>
          ${(facture.items || [])
            .map(
              (item) => `
            <tr>
              <td>${item.description || ''}</td>
              <td>${item.quantity || item.qty || 1}</td>
              <td>${formatNumber(item.priceHT || item.price || 0)} D</td>
              <td>${((item.tva ?? item.tax ?? 0) * 100).toFixed(0)}%</td>
              <td>${formatNumber(item.totalHT || 0)} D</td>
              <td>${formatNumber(item.totalTTC || 0)} D</td>
            </tr>
          `
            )
            .join('')}
        </tbody>
      </table>

      <!-- Totaux -->
      <div class="totals">
        <div class="total-row">
          <span>Sous-total HT:</span>
          <span><strong>${totals.subtotal} D</strong></span>
        </div>
        <div class="total-row">
          <span>TVA:</span>
          <span><strong>${totals.tva} D</strong></span>
        </div>
        <div class="total-row">
          <span>Timbre fiscal:</span>
          <span><strong>${totals.timbre} D</strong></span>
        </div>
        <div class="total-row grand-total">
          <span>TOTAL TTC:</span>
          <span><strong>${totals.total} D</strong></span>
        </div>
      </div>

      ${
        facture.notes
          ? `
        <div class="notes">
          <h4>Notes:</h4>
          <p>${facture.notes}</p>
        </div>
      `
          : ''
      }

      <div class="payment-terms">
        <h4>Conditions de paiement:</h4>
        <p>${
          cabinetInfo?.paymentTerms ||
          'Paiement par virement bancaire dans les 30 jours'
        }</p>
        ${
          cabinetInfo?.rib
            ? `<p><strong>RIB:</strong> ${cabinetInfo.rib}</p>`
            : ''
        }
        ${
          cabinetInfo?.bank
            ? `<p><strong>Banque:</strong> ${cabinetInfo.bank}</p>`
            : ''
        }
      </div>

      <div class="footer">
        <div class="client-signature">
          <p>Pour le client,</p>
          <div class="signature-area"></div>
        </div>
        <div class="cabinet-signature">
          <p>Pour le cabinet,</p>
          <div class="signature-area"></div>
        </div>
      </div>
    </div>
  `;
};
